import React, { useState, useMemo, useCallback, MutableRefObject, useRef } from 'react';
import { IEditorProps } from '../Editor';
import { IPropertyChild, getChildValueAsArray } from 'components/property-editors/utils/ProviderNodeUtils';
import { FormTriggerData, newFormTriggerData } from '@decisions/server/Decisions/Silverlight/UI/Forms';
import { FormTriggerSourceData } from '@decisions/server/Decisions/Silverlight/UI/Forms';
import { FormTriggerType } from '@decisions/server/DecisionsFramework/ComponentData';
import './FormTriggerDataEditor.less';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { FormTriggerDataEditorItem } from './FormTriggerDataEditorItem';
import { useEditorId } from '../hooks/useEditorId';
import { EditorLabel } from '../EditorLabel';
import { SearchField } from 'components/inputs/SearchField';
import { Label } from 'components/inputs/Label';
import { ORDER_BY_OPTIONS, PRIMARY_CLASS, ORDER_BY_OPTION_CONTROL_TYPE } from './FormTriggerDataEditorConstants';
import { ListBoxEmptyMessage } from 'components/lists/ListBoxEmptyMessage';
import { PickButton } from 'components/buttons/PickButton/PickButton';
import { Callout } from 'components/layout/Callout';
import { ActionLinks } from 'components/ActionLinks/ActionLinks';
import { HasOpenState } from 'hooks/HasOpenState';
interface IFormTriggerDataEditorProps extends IEditorProps {}

/**
 * FormTriggerDataEditor
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */

export function FormTriggerDataEditor({ child, path, onChange }: IFormTriggerDataEditorProps) {
  const [selected, setSelected] = useState<FormTriggerData[]>(getChildValueAsArray(child));
  const { isOpen, toggleOpen } = HasOpenState();
  const linkRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);

  const [searchString, setSearchString] = useState('');
  const [orderBy, setOrderBy] = useState<string>(ORDER_BY_OPTIONS[0]);

  const options = useMemo(() => getOptions(child), [child]);
  const [id] = useEditorId(path);

  const isSelected = useCallback(
    (value: FormTriggerType, componentName: string) => {
      const triggerData = selected.find(s => s.ComponentName === componentName);
      if (!triggerData || !triggerData.Events) {
        return false;
      }
      return triggerData.Events.find(e => e === value) !== undefined;
    },
    [selected]
  );

  const onSearch = useCallback((search: string) => {
    setSearchString(search);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchString('');
  }, []);

  const changeHandler = useCallback(
    (value: FormTriggerType, componentName: string) => {
      const newValue = [...selected];

      const item = selected.find(v => v.ComponentName === componentName);
      if (item) {
        const newEvents = item.Events || [];
        if (newEvents.includes(value)) {
          newEvents.splice(newEvents.indexOf(value), 1);
        } else {
          newEvents.push(value);
        }
        item.Events = newEvents;
      } else {
        const newItem = newFormTriggerData({ ComponentName: componentName, Events: [value] });
        newValue.push(newItem);
      }

      setSelected(newValue);
      onChange(newValue, path);
    },
    [selected, onChange, path]
  );

  const onOrderChanged = useCallback(
    (search: string) => {
      setOrderBy(search);
      toggleOpen();
    },
    [toggleOpen]
  );

  const getFilteredData = () => {
    const data = options.filter(i => i.ComponentName.toUpperCase().includes(searchString.toUpperCase()));
    if (orderBy === ORDER_BY_OPTION_CONTROL_TYPE) {
      return data.sort((a, b) => a.ControlTypeName.localeCompare(b.ControlTypeName));
    }
    return data.sort((a, b) => a.ComponentName.localeCompare(b.ComponentName));
  };
  const hasValue = () => {
    return options && options.length > 0;
  };
  return (
    <EditorLabel htmlFor={id} editor={child} path={path} doNotWrap>
      {hasValue() && <SearchField onSearch={onSearch} name={'Search'} value={searchString} onClear={clearSearch} />}
      <div className={`${PRIMARY_CLASS}__holder`}>
        <div className={PRIMARY_CLASS}>
          {getFilteredData().map(
            item =>
              item && (
                <FormTriggerDataEditorItem
                  id={id}
                  item={item}
                  key={item.ComponentName}
                  changeHandler={changeHandler}
                  isSelected={isSelected}
                />
              )
          )}
          <ListBoxEmptyMessage list={options} text={'No data to display'} />
        </div>
        {hasValue() && (
          <Label
            label="Order By:"
            htmlFor={`${id}_orderby`}
            actions={<PickButton onClick={toggleOpen} text={orderBy} buttonRef={linkRef} />}
            placement={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Callout
              isOpen={isOpen}
              onClose={toggleOpen}
              sourceRef={linkRef}
              placement="bottom"
              showArrow={true}
              flipBehavior="counterclockwise"
            >
              <ActionLinks
                values={{
                  current: ORDER_BY_OPTIONS.map(s => {
                    return { label: s, value: s };
                  }),
                }}
                callback={onOrderChanged}
              />
            </Callout>
          </Label>
        )}
      </div>
    </EditorLabel>
  );
}

function getOptions(child: IPropertyChild): FormTriggerSourceData[] {
  return (getEditorConfigValue(child, 'values') || []).filter((value?: FormTriggerSourceData | null) => {
    if (!value) {
      console.log('Improper Configuration: falsy value passed as FormTriggerSourceData entry name');
      return false;
    }
    return true;
  });
}
/*
   <Label label="Order By:" htmlFor={`${id}_orderby`}>
            <StringComboBox
              id={`${id}_orderby`}
              options={ORDER_BY_OPTIONS}
              currentValue={orderBy as string}
              onValueChange={onOrderChanged}
              allowFreeText={false}
              hideClear={true}
            />
          </Label>
*/
